/**
 * 소요 분출용량 계산기 — 시나리오 행 안에 펼쳐지는 패널.
 *
 * 13·14·15번 시나리오에만 붙는다. 나머지는 표준에 계산식이 없어
 * 직접 입력이 맞다.
 *
 * 결과는 자동으로 반영하지 않는다. **사용자가 '분출용량에 넣기'를 눌러야**
 * 값이 옮겨간다 — 계산기를 여닫는 것만으로 이미 확정한 유량이 조용히
 * 덮이면 안 되기 때문이다.
 */
import { useMemo } from "react";
import { CUBIC_EXPANSION_TABLE } from "../calc/thermalExpansion";
import { ENVIRONMENT_FACTORS } from "../calc/fireCase";
import { Field, Help } from "./formBits";
import { gaugeLabel, type PressureUnit } from "./pressureUnit";
import {
  RATE_CALC_LABELS,
  kindsForItem,
  runRateCalc,
  wettedAreaOf,
  type RateCalcContext,
  type RateCalcKind,
  type RateCalcState,
  type WettedGeometry,
} from "./rateCalc";

export function RateCalcPanel({
  item,
  state,
  ctx,
  unit,
  onChange,
  onApply,
}: {
  item: number;
  state: RateCalcState;
  ctx: RateCalcContext;
  unit: PressureUnit;
  onChange: (next: RateCalcState) => void;
  /** 계산 결과를 '소요 분출용량' 칸으로 옮긴다. */
  onApply: (value: string) => void;
}) {
  const set = <K extends keyof RateCalcState>(k: K, v: RateCalcState[K]) =>
    onChange({ ...state, [k]: v });

  const kinds = kindsForItem(item);
  const outcome = useMemo(() => runRateCalc(state, ctx), [state, ctx]);
  const wetted = state.kind === "fireWetted" ? wettedAreaOf(state) : null;

  if (kinds.length === 0) return null;

  return (
    <div className="ratecalc">
      <div className="ratecalc__head">
        <strong>소요 분출용량 계산</strong>
        <span className="muted">
          {state.kind === "thermal" ||
          state.kind === "fireWetted" ||
          state.kind === "fireUnwetted" ||
          state.kind === "tubeLiquid" ||
          state.kind === "tubeGas"
            ? "API 521 근거"
            : state.kind === "cvValveLiquid" || state.kind === "cvValveGas"
              ? "ISA/IEC 밸브 Cv 근거 — KOSHA·API 521 식은 아님"
              : "일반 유체역학 — 표준식 아님"}
        </span>
        <button
          type="button"
          className="btn btn--ghost"
          onClick={() => set("open", !state.open)}
          aria-expanded={state.open}
        >
          {state.open ? "닫기" : "열기"}
        </button>
      </div>

      {state.open && (
        <div className="ratecalc__body">
          {kinds.length > 1 && (
            <Field label="산정 방법" unit="" help="rateCalcKind">
              <select
                value={state.kind}
                onChange={(e) => set("kind", e.target.value as RateCalcKind)}
              >
                {kinds.map((k) => (
                  <option key={k} value={k}>
                    {RATE_CALC_LABELS[k]}
                  </option>
                ))}
              </select>
            </Field>
          )}
          {kinds.length === 1 && (
            <p className="hint">{RATE_CALC_LABELS[state.kind]}</p>
          )}

          {/* ── 정변위 펌프 최대유입 (액체) ── */}
          {state.kind === "pdPumpLiquid" && (
            <div className="grid-form">
              <Field label="정격 토출량" unit="m³/h" help="pdRatedFlow">
                <input
                  value={state.pdRatedFlow}
                  onChange={(e) => set("pdRatedFlow", e.target.value)}
                />
              </Field>
              <Field label="배출 조건 밀도" unit="kg/m³" help="pdDensity">
                <input
                  value={state.pdDensity}
                  onChange={(e) => set("pdDensity", e.target.value)}
                />
              </Field>
              <Field label="계수" unit="" help="pdCapacityFactor">
                <input
                  value={state.pdCapacityFactor}
                  onChange={(e) => set("pdCapacityFactor", e.target.value)}
                />
              </Field>
            </div>
          )}

          {/* ── 정변위 압축기 최대유입 (기체) ── */}
          {state.kind === "pdPumpGas" && (
            <div className="grid-form">
              <Field label="정격 토출량(흡입조건)" unit="m³/h" help="pdRatedFlow">
                <input
                  value={state.pdRatedFlow}
                  onChange={(e) => set("pdRatedFlow", e.target.value)}
                />
              </Field>
              <Field
                label="흡입측 압력"
                unit={gaugeLabel(unit)}
                help="pdSuctionPressure"
              >
                <input
                  value={state.pdSuctionPressure}
                  onChange={(e) => set("pdSuctionPressure", e.target.value)}
                />
              </Field>
              <Field label="흡입측 온도" unit="K" help="pdSuctionTemperature">
                <input
                  value={state.pdSuctionTemperature}
                  onChange={(e) => set("pdSuctionTemperature", e.target.value)}
                />
              </Field>
              <Field label="분자량" unit="kg/kmol" help="pdMolecularWeight">
                <input
                  value={state.pdMolecularWeight}
                  onChange={(e) => set("pdMolecularWeight", e.target.value)}
                />
              </Field>
              <Field label="압축계수 Z" unit="" help="pdCompressibility">
                <input
                  value={state.pdCompressibility}
                  onChange={(e) => set("pdCompressibility", e.target.value)}
                />
              </Field>
              <Field label="계수" unit="" help="pdCapacityFactor">
                <input
                  value={state.pdCapacityFactor}
                  onChange={(e) => set("pdCapacityFactor", e.target.value)}
                />
              </Field>
            </div>
          )}

          {/* ── 배관 최대유입 — 표준유속 기준 ── */}
          {state.kind === "pipingVelocity" && (
            <div className="grid-form">
              <Field label="관 내경" unit="mm" help="pipeInnerDiameter">
                <input
                  value={state.pipeInnerDiameter}
                  onChange={(e) => set("pipeInnerDiameter", e.target.value)}
                />
              </Field>
              <Field label="적용 유속" unit="m/s" help="pipeVelocity">
                <input
                  value={state.pipeVelocity}
                  onChange={(e) => set("pipeVelocity", e.target.value)}
                />
              </Field>
              <Field label="밀도" unit="kg/m³" help="pipeDensity">
                <input
                  value={state.pipeDensity}
                  onChange={(e) => set("pipeDensity", e.target.value)}
                />
              </Field>
            </div>
          )}

          {/* ── 배관 최대유입 — 배관 수력학 역산 ── */}
          {state.kind === "pipeHydraulics" && (
            <div className="grid-form">
              <Field label="관 내경" unit="mm" help="phInnerDiameter">
                <input
                  value={state.phInnerDiameter}
                  onChange={(e) => set("phInnerDiameter", e.target.value)}
                />
              </Field>
              <Field label="직관 길이" unit="m" help="phStraightLength">
                <input
                  value={state.phStraightLength}
                  onChange={(e) => set("phStraightLength", e.target.value)}
                />
              </Field>
              <Field label="부속 등가길이 합" unit="m" help="phFittingLength">
                <input
                  value={state.phFittingLength}
                  onChange={(e) => set("phFittingLength", e.target.value)}
                />
              </Field>
              <Field label="절대조도" unit="mm" help="phRoughness">
                <input
                  value={state.phRoughness}
                  onChange={(e) => set("phRoughness", e.target.value)}
                />
              </Field>
              <Field
                label="상류(구동측) 압력"
                unit={gaugeLabel(unit)}
                help="phUpstreamPressure"
              >
                <input
                  value={state.phUpstreamPressure}
                  onChange={(e) => set("phUpstreamPressure", e.target.value)}
                />
              </Field>
              <Field
                label="하류(받는측) 압력"
                unit={gaugeLabel(unit)}
                help="phDownstreamPressure"
              >
                <input
                  value={state.phDownstreamPressure}
                  onChange={(e) => set("phDownstreamPressure", e.target.value)}
                />
              </Field>
              <Field label="밀도" unit="kg/m³" help="phDensity">
                <input
                  value={state.phDensity}
                  onChange={(e) => set("phDensity", e.target.value)}
                />
              </Field>
              <Field label="점도" unit="cP" help="phViscosity">
                <input
                  value={state.phViscosity}
                  onChange={(e) => set("phViscosity", e.target.value)}
                />
              </Field>
            </div>
          )}

          {/* ── 밸브 고장 — Cv 정식 계산 (액체/기체 공통 입력) ── */}
          {(state.kind === "cvValveLiquid" || state.kind === "cvValveGas") && (
            <div className="grid-form">
              <Field label="Cv" unit="" help="cvValue">
                <input
                  value={state.cvValue}
                  onChange={(e) => set("cvValue", e.target.value)}
                />
              </Field>
              <Field
                label="상류(감압 전) 압력"
                unit={gaugeLabel(unit)}
                help="cvUpstreamPressure"
              >
                <input
                  value={state.cvUpstreamPressure}
                  onChange={(e) => set("cvUpstreamPressure", e.target.value)}
                />
              </Field>
              <Field
                label="하류(감압 후) 압력"
                unit={gaugeLabel(unit)}
                help="cvDownstreamPressure"
              >
                <input
                  value={state.cvDownstreamPressure}
                  onChange={(e) => set("cvDownstreamPressure", e.target.value)}
                />
              </Field>
              {state.kind === "cvValveLiquid" ? (
                <>
                  <Field label="밀도" unit="kg/m³" help="cvDensity">
                    <input
                      value={state.cvDensity}
                      onChange={(e) => set("cvDensity", e.target.value)}
                    />
                  </Field>
                  <Field label="FL (정격 압력회복계수)" unit="" help="cvFL">
                    <input
                      value={state.cvFL}
                      onChange={(e) => set("cvFL", e.target.value)}
                    />
                  </Field>
                  <Field label="증기압" unit="kPa abs" help="cvVaporPressure">
                    <input
                      value={state.cvVaporPressure}
                      onChange={(e) => set("cvVaporPressure", e.target.value)}
                    />
                  </Field>
                  <Field
                    label="임계압력 (선택)"
                    unit="kPa abs"
                    help="cvCriticalPressure"
                  >
                    <input
                      value={state.cvCriticalPressure}
                      onChange={(e) =>
                        set("cvCriticalPressure", e.target.value)
                      }
                    />
                  </Field>
                </>
              ) : (
                <>
                  <Field label="온도" unit="K" help="cvTemperature">
                    <input
                      value={state.cvTemperature}
                      onChange={(e) => set("cvTemperature", e.target.value)}
                    />
                  </Field>
                  <Field label="분자량" unit="kg/kmol" help="cvMolecularWeight">
                    <input
                      value={state.cvMolecularWeight}
                      onChange={(e) =>
                        set("cvMolecularWeight", e.target.value)
                      }
                    />
                  </Field>
                  <Field label="비열비 k" unit="" help="cvSpecificHeatRatio">
                    <input
                      value={state.cvSpecificHeatRatio}
                      onChange={(e) =>
                        set("cvSpecificHeatRatio", e.target.value)
                      }
                    />
                  </Field>
                  <Field label="압축계수 Z" unit="" help="cvCompressibility">
                    <input
                      value={state.cvCompressibility}
                      onChange={(e) =>
                        set("cvCompressibility", e.target.value)
                      }
                    />
                  </Field>
                  <Field label="xT (정격 압력강하비 계수)" unit="" help="cvXt">
                    <input
                      value={state.cvXt}
                      onChange={(e) => set("cvXt", e.target.value)}
                    />
                  </Field>
                </>
              )}
            </div>
          )}

          {/* ── 제어밸브·체크밸브 고장 (액체/기체 공통 입력) ── */}
          {(state.kind === "valveFailureLiquid" ||
            state.kind === "valveFailureGas") && (
            <div className="grid-form">
              <Field label="관(밸브) 내경" unit="mm" help="valveInnerDiameter">
                <input
                  value={state.valveInnerDiameter}
                  onChange={(e) => set("valveInnerDiameter", e.target.value)}
                />
              </Field>
              <Field
                label="상류(감압 전) 압력"
                unit={gaugeLabel(unit)}
                help="valveUpstreamPressure"
              >
                <input
                  value={state.valveUpstreamPressure}
                  onChange={(e) => set("valveUpstreamPressure", e.target.value)}
                />
              </Field>
              <Field
                label="하류(감압 후) 압력"
                unit={gaugeLabel(unit)}
                help="valveDownstreamPressure"
              >
                <input
                  value={state.valveDownstreamPressure}
                  onChange={(e) =>
                    set("valveDownstreamPressure", e.target.value)
                  }
                />
              </Field>
              {state.kind === "valveFailureLiquid" ? (
                <Field label="밀도" unit="kg/m³" help="valveDensity">
                  <input
                    value={state.valveDensity}
                    onChange={(e) => set("valveDensity", e.target.value)}
                  />
                </Field>
              ) : (
                <>
                  <Field label="온도" unit="K" help="valveTemperature">
                    <input
                      value={state.valveTemperature}
                      onChange={(e) => set("valveTemperature", e.target.value)}
                    />
                  </Field>
                  <Field label="분자량" unit="kg/kmol" help="valveMolecularWeight">
                    <input
                      value={state.valveMolecularWeight}
                      onChange={(e) =>
                        set("valveMolecularWeight", e.target.value)
                      }
                    />
                  </Field>
                  <Field label="비열비 k" unit="" help="valveSpecificHeatRatio">
                    <input
                      value={state.valveSpecificHeatRatio}
                      onChange={(e) =>
                        set("valveSpecificHeatRatio", e.target.value)
                      }
                    />
                  </Field>
                  <Field label="압축계수 Z" unit="" help="valveCompressibility">
                    <input
                      value={state.valveCompressibility}
                      onChange={(e) =>
                        set("valveCompressibility", e.target.value)
                      }
                    />
                  </Field>
                </>
              )}
              <Field label="배출계수 Cd" unit="" help="valveCd">
                <input
                  value={state.valveCd}
                  onChange={(e) => set("valveCd", e.target.value)}
                />
              </Field>
            </div>
          )}

          {/* ── 화재: 액체가 있는 용기 ── */}
          {state.kind === "fireWetted" && (
            <>
              <div className="grid-form">
                <Field label="용기 형상" unit="" help="wettedGeometry">
                  <select
                    value={state.geometry}
                    onChange={(e) =>
                      set("geometry", e.target.value as WettedGeometry)
                    }
                  >
                    <option value="vertical">수직 원통</option>
                    <option value="horizontal">수평 원통</option>
                    <option value="sphere">구형</option>
                    <option value="manual">직접 입력</option>
                  </select>
                </Field>
                {state.geometry === "manual" ? (
                  <Field label="습윤면적 Aws" unit="m²" help="wettedArea">
                    <input
                      value={state.wettedArea}
                      onChange={(e) => set("wettedArea", e.target.value)}
                    />
                  </Field>
                ) : (
                  <>
                    <Field label="안지름" unit="m" help="vesselDiameter">
                      <input
                        value={state.diameter}
                        onChange={(e) => set("diameter", e.target.value)}
                      />
                    </Field>
                    {state.geometry === "horizontal" && (
                      <Field label="동체 길이" unit="m" help="vesselLength">
                        <input
                          value={state.length}
                          onChange={(e) => set("length", e.target.value)}
                        />
                      </Field>
                    )}
                    <Field label="액위" unit="m" help="liquidHeight">
                      <input
                        value={state.liquidHeight}
                        onChange={(e) => set("liquidHeight", e.target.value)}
                      />
                    </Field>
                  </>
                )}
                <Field label="증발잠열" unit="J/kg" help="latentHeat">
                  <input
                    value={state.latentHeat}
                    onChange={(e) => set("latentHeat", e.target.value)}
                  />
                </Field>
                <Field label="환경계수 F" unit="" help="environmentFactor">
                  <select
                    value={state.environmentFactor}
                    onChange={(e) => set("environmentFactor", e.target.value)}
                    disabled={!state.insulated}
                  >
                    {ENVIRONMENT_FACTORS.map((f) => (
                      <option key={f.label} value={String(f.f)}>
                        {f.label} — {f.f}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              {wetted !== null && Number.isFinite(wetted) && (
                <p className="hint">
                  계산된 습윤면적{" "}
                  <b>
                    {wetted.toLocaleString("ko-KR", {
                      maximumFractionDigits: 2,
                    })}{" "}
                    m²
                  </b>
                  {state.geometry !== "manual" &&
                    " — 헤드·스커트·연결 배관 표면은 포함되지 않았습니다."}
                </p>
              )}

              <div className="ratecalc__checks">
                <label>
                  <input
                    type="checkbox"
                    checked={state.insulated}
                    onChange={(e) => set("insulated", e.target.checked)}
                  />
                  화재 등급 보온 있음
                  <Help topic="insulated" />
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={state.drainage}
                    onChange={(e) => set("drainage", e.target.checked)}
                  />
                  적절한 배수·소방설비 있음 (식 7 / 없으면 식 8)
                  <Help topic="drainage" />
                </label>
              </div>
            </>
          )}

          {/* ── 화재: 기체만 있는 용기 ── */}
          {state.kind === "fireUnwetted" && (
            <>
              <p className="hint">
                이 경로는 표준이 <b>유량이 아니라 소요 면적을 바로 줍니다.</b>{" "}
                보온 효과는 인정되지 않습니다 (API 521 4.4.13.2.4.3).
              </p>
              <div className="grid-form">
                <Field label="노출 표면적 A′" unit="m²" help="exposedArea">
                  <input
                    value={state.exposedArea}
                    onChange={(e) => set("exposedArea", e.target.value)}
                  />
                </Field>
                <Field
                  label="정상 운전 압력"
                  unit={gaugeLabel(unit)}
                  help="normalPressure"
                >
                  <input
                    value={state.normalPressure}
                    onChange={(e) => set("normalPressure", e.target.value)}
                  />
                </Field>
                <Field label="정상 운전 온도" unit="K" help="normalTemperature">
                  <input
                    value={state.normalTemperature}
                    onChange={(e) => set("normalTemperature", e.target.value)}
                  />
                </Field>
                <Field label="최대 벽면 온도 Tw" unit="K" help="wallTemperature">
                  <input
                    value={state.wallTemperature}
                    onChange={(e) => set("wallTemperature", e.target.value)}
                  />
                </Field>
              </div>
              <div className="ratecalc__checks">
                <label>
                  <input
                    type="checkbox"
                    checked={state.useDefaultFPrime}
                    onChange={(e) => set("useDefaultFPrime", e.target.checked)}
                  />
                  정보 부족 — 기본값 F′ = 821 사용
                  <Help topic="useDefaultFPrime" />
                </label>
              </div>
              <p className="hint">
                비열비 k 는 이 시나리오의 기체 물성을 그대로 씁니다.
              </p>
            </>
          )}

          {/* ── 액체 열팽창 ── */}
          {state.kind === "thermal" && (
            <>
              <div className="grid-form">
                <Field label="체적팽창계수 αv" unit="1/℃" help="alphaV">
                  <input
                    value={state.alphaV}
                    onChange={(e) => set("alphaV", e.target.value)}
                    list="cubic-expansion-values"
                  />
                  <select
                    aria-label="API 521 Table 2 에서 αv 선택"
                    value={
                      CUBIC_EXPANSION_TABLE.some(
                        (r) => String(r.alphaV) === state.alphaV,
                      )
                        ? state.alphaV
                        : "custom"
                    }
                    onChange={(e) => {
                      if (e.target.value !== "custom") {
                        set("alphaV", e.target.value);
                      }
                    }}
                  >
                    <option value="custom">직접 입력 (공정 자료)</option>
                    {CUBIC_EXPANSION_TABLE.map((r) => (
                      <option key={r.label} value={String(r.alphaV)}>
                        {r.label} — {r.alphaV}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="총 전열량 φ" unit="W" help="heatRate">
                  <input
                    value={state.heatRate}
                    onChange={(e) => set("heatRate", e.target.value)}
                  />
                </Field>
                <Field label="비중 d" unit="" help="relativeDensity">
                  <input
                    value={state.relativeDensity}
                    onChange={(e) => set("relativeDensity", e.target.value)}
                  />
                </Field>
                <Field label="비열 c" unit="J/kg·K" help="specificHeatCapacity">
                  <input
                    value={state.specificHeat}
                    onChange={(e) => set("specificHeat", e.target.value)}
                  />
                </Field>
              </div>
              <datalist id="cubic-expansion-values">
                {CUBIC_EXPANSION_TABLE.map((r) => (
                  <option key={r.label} value={String(r.alphaV)}>
                    {r.label}
                  </option>
                ))}
              </datalist>
              <p className="hint">
                αv 는 <b>온도가 1 ℃ 오를 때 액체 부피가 늘어나는 비율</b>
                입니다. 무거운(끈적한) 액체일수록 값이 작고, 가벼운
                액체일수록 값이 크며, 물은 석유류와 자릿수 자체가 다릅니다
                (0.00018).
              </p>
              <p className="hint">
                αv 는 공정 설계자료(물성표)가 가장 정확합니다. 없으면 위
                입력란 아래의 <b>API 521 Table 2</b> 선택 목록(15.6 ℃
                기준)에서 근사할 수 있습니다. 목록의{" "}
                <b>°API 도(API gravity)</b>는 원유·석유제품의 비중을 나타내는
                업계 관행 단위로, <b>값이 클수록 가벼운(밀도가 낮은) 유체</b>
                입니다 — 물이 10°API 입니다. 비중(SG, 15.6 ℃ 물 = 1.00)만
                안다면 <code>°API = 141.5 / SG − 131.5</code> 로 환산하십시오.
                공정 자료·SDS 에 비중이나 °API 가 이미 적혀 있는 경우가
                많습니다.
              </p>
            </>
          )}

          {/* ── 튜브 파열 ── */}
          {(state.kind === "tubeLiquid" || state.kind === "tubeGas") && (
            <>
              <p className="hint">
                표준은 튜브 하나가 튜브시트 뒷면에서 예리하게 절단되고, 고압
                유체가 <b>양쪽 끝 2개소</b>로 나온다고 봅니다 (API 521
                4.4.14.2.2). 저압측 압력은 이 밸브의 배출 압력이 자동으로
                쓰입니다.
              </p>
              <div className="grid-form">
                <Field label="튜브 안지름" unit="mm" help="tubeInnerDiameter">
                  <input
                    value={state.tubeId}
                    onChange={(e) => set("tubeId", e.target.value)}
                  />
                </Field>
                <Field
                  label="고압측 압력"
                  unit={gaugeLabel(unit)}
                  help="highSidePressure"
                >
                  <input
                    value={state.highSidePressure}
                    onChange={(e) => set("highSidePressure", e.target.value)}
                  />
                </Field>
                <Field
                  label="배출계수 Cd"
                  unit=""
                  help="tubeDischargeCoefficient"
                >
                  <input
                    value={state.tubeCd}
                    onChange={(e) => set("tubeCd", e.target.value)}
                  />
                </Field>
                {state.kind === "tubeLiquid" && (
                  <Field label="고압측 밀도" unit="kg/m³" help="tubeDensity">
                    <input
                      value={state.tubeDensity}
                      onChange={(e) => set("tubeDensity", e.target.value)}
                    />
                  </Field>
                )}
                {state.kind === "tubeGas" && (
                  <>
                    <Field label="고압측 온도" unit="K" help="temperatureK">
                      <input
                        value={state.tubeTemperature}
                        onChange={(e) => set("tubeTemperature", e.target.value)}
                      />
                    </Field>
                    <Field label="분자량" unit="kg/kmol" help="molecularWeight">
                      <input
                        value={state.tubeMolecularWeight}
                        onChange={(e) =>
                          set("tubeMolecularWeight", e.target.value)
                        }
                      />
                    </Field>
                    <Field label="비열비 k" unit="" help="specificHeatRatio">
                      <input
                        value={state.tubeSpecificHeatRatio}
                        onChange={(e) =>
                          set("tubeSpecificHeatRatio", e.target.value)
                        }
                      />
                    </Field>
                    <Field label="압축계수 Z" unit="" help="compressibility">
                      <input
                        value={state.tubeCompressibility}
                        onChange={(e) =>
                          set("tubeCompressibility", e.target.value)
                        }
                      />
                    </Field>
                  </>
                )}
              </div>
              <p className="hint">
                고압측 유체가 파단부를 지나며 <b>플래싱하면 이 계산을 쓰면
                안 됩니다.</b> 이상유동(HEM) 방법으로 유량을 구한 뒤 직접
                입력하고, 밸브는 2상 유동 사이징으로 검토하십시오.
              </p>
            </>
          )}

          {/* ── 결과 ── */}
          <div
            className={
              outcome.ok ? "ratecalc__out" : "ratecalc__out ratecalc__out--bad"
            }
          >
            {!outcome.ok ? (
              <p>{outcome.message}</p>
            ) : (
              <>
                <dl className="result__grid">
                  {outcome.lines.map((l) => (
                    <div key={l.label}>
                      <dt>{l.label}</dt>
                      <dd>{l.value}</dd>
                    </div>
                  ))}
                </dl>
                {outcome.equation && (
                  <p className="muted">적용 식: {outcome.equation}</p>
                )}
                {outcome.apply ? (
                  <div className="ratecalc__apply">
                    <button
                      type="button"
                      className="btn"
                      onClick={() => onApply(outcome.apply!.value)}
                    >
                      소요 분출용량에 넣기 ({outcome.apply.value}{" "}
                      {outcome.apply.unit})
                    </button>
                    {outcome.apply.note && (
                      <span className="muted">{outcome.apply.note}</span>
                    )}
                  </div>
                ) : (
                  <p className="muted">
                    이 결과는 분출용량 칸에 바로 넣을 수 없습니다. 아래 안내를
                    확인하십시오.
                  </p>
                )}
              </>
            )}
          </div>

          {outcome.warnings.length > 0 && (
            <ul className="warnings">
              {outcome.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
